import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
	type CreateOutboundPlaybookRunForCompanyResponse,
	type OutboundPayload,
	type OutboundPlaybook,
	type OutboundPlaybookRun,
	type OutboundTask,
	type OutboundTaskAttempt,
	parsePhoneNumberValue,
} from "@repo/common";
import {
	cancelOutboundPlaybookRunForCompany,
	chunkValues,
	createOutboundPlaybookRunForCompany,
	db,
	forceRetryOutboundTaskForCompany,
	formatDateTime,
	formatDuration,
	groupAttemptsByTaskId,
	groupTasksByRunId,
	outboundPlaybookRunsCollection,
	outboundPlaybooksCollection,
	outboundTaskAttemptsCollection,
	outboundTasksCollection,
	pauseOutboundPlaybookRunForCompany,
	resumeOutboundPlaybookRunForCompany,
	runPlaybookName,
	runSuccessRate,
	terminalTaskCount,
	timeValue,
} from "@repo/ui";
import { format } from "date-fns";
import { onSnapshot, query, where } from "firebase/firestore";
import {
	AlertCircle,
	CheckCircle2,
	Download,
	Eye,
	EyeOff,
	FileJson,
	ListChecks,
	Loader2,
	MoreHorizontal,
	Pause,
	Play,
	Plus,
	RotateCcw,
	Search,
	Square,
} from "lucide-react";
import { type FormEvent, Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { useOutboundPlaybookFeatureFlag } from "../hooks/useOutboundPlaybookFeatureFlag";
import { i18n } from "../i18n";
import { formatPhoneNumber, isPhoneKey } from "../utils/formatPhoneNumber";
import {
	type BatchParseResult,
	parseSpreadsheetFile,
	validateAndMapBatchRows,
} from "../utils/playbookRunBatchParser";
import { getContentFieldProps } from "../utils/textDirection";
import { DateRangeFilter } from "./DateRangeFilter";
import { PageHeader } from "./PageHeader";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "./ui/pagination";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { useIsMobile } from "./ui/use-mobile";

type RunStatusFilter = "all" | OutboundPlaybookRun["status"];
type SchemaField = {
	name: string;
	type: "string" | "number" | "boolean" | "date" | "datetime" | "phone_number";
	required: boolean;
	description: string;
	enumValues: string[];
	columnName?: string;
};
type RunFormValues = Record<string, string | boolean>;
type TechnicalDetails = {
	title: string;
	document: unknown;
};

function statusLabel(status: string): string {
	switch (status) {
		case "pending":
			return t({ id: "playbookRuns.status.pending", message: "Pending" });
		case "scheduled":
			return t({ id: "playbookRuns.status.scheduled", message: "Scheduled" });
		case "running":
		case "in_progress":
			return t({ id: "playbookRuns.status.running", message: "Running" });
		case "dispatching":
		case "starting":
		case "claimed":
			return t({ id: "playbookRuns.status.starting", message: "Starting" });
		case "retry_wait":
			return t({ id: "playbookRuns.status.retryWait", message: "Retrying" });
		case "paused":
			return t({ id: "playbookRuns.status.paused", message: "Paused" });
		case "completed":
			return t({ id: "playbookRuns.status.completed", message: "Completed" });
		case "partially_failed":
			return t({
				id: "playbookRuns.status.partiallyFailed",
				message: "Partially failed",
			});
		case "failed":
			return t({ id: "playbookRuns.status.failed", message: "Failed" });
		case "abandoned":
			return t({ id: "playbookRuns.status.abandoned", message: "Abandoned" });
		case "cancelled":
			return t({ id: "playbookRuns.status.cancelled", message: "Cancelled" });
		default:
			return status.replaceAll("_", " ");
	}
}

function recoveryReviewLabel(reason: string): string {
	if (reason === "provider_start_ambiguous") {
		return t({
			id: "playbookRuns.recoveryReview.startUnconfirmed",
			message: "Call start could not be confirmed",
		});
	}
	if (
		reason === "conversation_possible_result_missing" ||
		reason === "stale_result_unknown"
	) {
		return t({
			id: "playbookRuns.recoveryReview.resultUnavailable",
			message: "Call result is unavailable",
		});
	}
	return t({
		id: "playbookRuns.recoveryReview.investigate",
		message: "Call needs investigation",
	});
}

function statusBadgeClass(status: string): string {
	if (status === "completed") {
		return "border-live/30 bg-live-tint text-foreground";
	}
	if (status === "failed" || status === "abandoned") {
		return "border-destructive/30 bg-danger-tint text-danger-ink";
	}
	if (status === "partially_failed" || status === "retry_wait") {
		return "border-warn/30 bg-warn-tint text-foreground";
	}
	if (status === "running" || status === "in_progress") {
		return "border-ring/30 bg-accent text-accent-foreground";
	}
	if (status === "paused" || status === "cancelled") {
		return "border-border bg-secondary text-muted-foreground";
	}
	return "border-border bg-secondary text-muted-foreground";
}

function schemaFields(schema: OutboundPayload): SchemaField[] {
	const properties =
		schema.properties &&
		typeof schema.properties === "object" &&
		!Array.isArray(schema.properties)
			? (schema.properties as Record<string, Record<string, unknown>>)
			: {};
	const required = Array.isArray(schema.required)
		? schema.required.filter(
				(value): value is string => typeof value === "string",
			)
		: [];

	return Object.entries(properties).map(([name, property]) => {
		const type =
			property.format === "phone_number"
				? "phone_number"
				: property.format === "date"
					? "date"
					: property.format === "date-time"
						? "datetime"
						: property.type === "number" ||
								property.type === "boolean" ||
								property.type === "string"
							? property.type
							: "string";
		return {
			name,
			type,
			required: required.includes(name),
			description:
				typeof property.description === "string" ? property.description : "",
			columnName:
				typeof property.columnName === "string" ? property.columnName : "",
			enumValues: Array.isArray(property.enum)
				? property.enum.filter(
						(value): value is string => typeof value === "string",
					)
				: [],
		};
	});
}

function initialValues(fields: SchemaField[]): RunFormValues {
	return Object.fromEntries(
		fields.map((field) => [field.name, field.type === "boolean" ? false : ""]),
	);
}

function fieldLabel(field: SchemaField) {
	return field.required ? `${field.name} *` : field.name;
}

function buildInputPayload(fields: SchemaField[], values: RunFormValues) {
	const input: OutboundPayload = {};
	for (const field of fields) {
		const value = values[field.name];
		if (field.type === "boolean") {
			input[field.name] = value === true;
			continue;
		}
		if (typeof value !== "string" || value.trim() === "") continue;
		if (field.type === "number") {
			const numeric = Number(value);
			if (!Number.isFinite(numeric)) {
				throw new Error(
					i18n._({
						id: "playbookRuns.newRun.errors.invalidNumber",
						message: "{field} must be a valid number.",
						values: { field: field.name },
					}),
				);
			}
			input[field.name] = numeric;
			continue;
		}
		if (field.type === "phone_number") {
			const phone = parsePhoneNumberValue(value);
			if (!phone) {
				throw new Error(
					i18n._({
						id: "playbookRuns.newRun.errors.invalidPhoneNumber",
						message: "{field} must be a valid phone number.",
						values: { field: field.name },
					}),
				);
			}
			input[field.name] = phone;
			continue;
		}
		input[field.name] = value.trim();
	}

	for (const field of fields) {
		if (!field.required) continue;
		const value = input[field.name];
		if (
			value === undefined ||
			value === null ||
			(typeof value === "string" && value.trim() === "")
		) {
			throw new Error(
				i18n._({
					id: "playbookRuns.newRun.errors.requiredField",
					message: "{field} is required.",
					values: { field: field.name },
				}),
			);
		}
	}
	return input;
}

function idempotencyKey() {
	const random =
		typeof crypto !== "undefined" && "randomUUID" in crypto
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random().toString(16).slice(2)}`;
	return `app-single-run-${random}`;
}

function payloadEntries(payload: OutboundPayload | null | undefined) {
	return Object.entries(payload || {});
}

function phoneFieldsFromSchema(
	schema: OutboundPayload | null | undefined,
): Set<string> {
	const properties =
		schema?.properties &&
		typeof schema.properties === "object" &&
		!Array.isArray(schema.properties)
			? (schema.properties as Record<string, Record<string, unknown>>)
			: {};
	return new Set(
		Object.entries(properties)
			.filter(([, prop]) => prop.format === "phone_number")
			.map(([name]) => name),
	);
}

function formatPayloadValue(value: unknown, key?: string): string {
	if (value === null || value === undefined) return "-";
	if (key && isPhoneKey(key)) {
		return formatPhoneNumber(value);
	}
	if (typeof value === "string") return formatPhoneNumber(value);
	if (typeof value === "number" || typeof value === "boolean") {
		return formatPhoneNumber(value);
	}
	return JSON.stringify(value);
}

function latestAttemptTime(attempt: OutboundTaskAttempt | undefined): unknown {
	return (
		attempt?.completedAt ||
		attempt?.failedAt ||
		attempt?.startedAt ||
		attempt?.createdAt
	);
}

function formatDateTimeParts(
	value: unknown,
): { date: string; time: string } | null {
	const timestamp = timeValue(value);
	if (!timestamp) return null;
	const date = new Date(timestamp);
	return {
		date: date.toLocaleDateString(),
		time: date.toLocaleTimeString(),
	};
}

function schemaHiddenFields(
	schema: OutboundPayload | null | undefined,
): Set<string> {
	const properties =
		schema?.properties &&
		typeof schema.properties === "object" &&
		!Array.isArray(schema.properties)
			? (schema.properties as Record<string, Record<string, unknown>>)
			: {};
	return new Set(
		Object.entries(properties)
			.filter(([, prop]) => prop.showInReport === false)
			.map(([name]) => name),
	);
}

function filterPayloadForMode(
	payload: OutboundPayload | null | undefined,
	detailMode: "basic" | "full",
	schemaFields: string[],
	hiddenFields?: Set<string>,
): OutboundPayload | null | undefined {
	if (!payload || detailMode === "full") return payload;
	const filtered: OutboundPayload = {};
	const allowedKeys =
		schemaFields.length > 0
			? schemaFields
			: Object.keys(payload).filter(
					(key) =>
						key !== "additionalFields" &&
						key !== "outcome" &&
						key !== "summary" &&
						key !== "error" &&
						key !== "errorCode",
				);

	for (const key of allowedKeys) {
		if (key in payload) {
			if (hiddenFields?.has(key)) {
				continue;
			}
			filtered[key] = payload[key];
		}
	}
	return filtered;
}

function escapeCsvCell(val: unknown): string {
	if (val === null || val === undefined) return '""';
	let str: string;
	if (typeof val === "object") {
		str = JSON.stringify(val);
	} else {
		str = String(val);
	}
	return `"${str.replace(/"/g, '""')}"`;
}

function downloadCsv(
	filename: string,
	headers: string[],
	rows: Array<Array<string | number>>,
) {
	const content = [
		headers.map(escapeCsvCell).join(","),
		...rows.map((row) => row.map(escapeCsvCell).join(",")),
	].join("\n");
	const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.setAttribute("href", url);
	link.setAttribute("download", filename);
	link.style.visibility = "hidden";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export function PlaybookRunsPage() {
	const { user, companyProfile, agents, language } = useData();
	const { i18n } = useLingui();
	const isMobile = useIsMobile();
	const [mobileAttempts, setMobileAttempts] = useState<Record<string, boolean>>(
		{},
	);
	const companyId = user?.companyId;
	const featureEnabled = useOutboundPlaybookFeatureFlag(
		companyId,
		companyProfile,
	);
	const [playbooks, setPlaybooks] = useState<OutboundPlaybook[]>([]);
	const [runs, setRuns] = useState<OutboundPlaybookRun[]>([]);
	const [tasks, setTasks] = useState<OutboundTask[]>([]);
	const [attempts, setAttempts] = useState<OutboundTaskAttempt[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [playbookFilter, setPlaybookFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState<RunStatusFilter>("all");
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [runsPerPage, setRunsPerPage] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [tasksPerPage, setTasksPerPage] = useState<number>(10);
	const [currentTasksPage, setCurrentTasksPage] = useState<number>(1);
	const [newRunDialogOpen, setNewRunDialogOpen] = useState(false);
	const [newRunPlaybookId, setNewRunPlaybookId] = useState("");
	const [newRunValues, setNewRunValues] = useState<RunFormValues>({});
	const [scheduleImmediately, setScheduleImmediately] = useState(true);
	const [submittingRun, setSubmittingRun] = useState(false);
	const [newRunTab, setNewRunTab] = useState<"single" | "batch">("single");
	const [batchFile, setBatchFile] = useState<File | null>(null);
	const [batchParsing, setBatchParsing] = useState(false);
	const [batchResult, setBatchResult] = useState<BatchParseResult | null>(null);
	const [batchServerResponse, setBatchServerResponse] =
		useState<CreateOutboundPlaybookRunForCompanyResponse | null>(null);
	const [retryingTaskIds, setRetryingTaskIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [pausingRunIds, setPausingRunIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [resumingRunIds, setResumingRunIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [cancellingRunIds, setCancellingRunIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [technicalDetails, setTechnicalDetails] =
		useState<TechnicalDetails | null>(null);
	const [showAttempts, setShowAttempts] = useState(false);
	const [detailMode, setDetailMode] = useState<"basic" | "full">("basic");

	useEffect(() => {
		if (!newRunDialogOpen) {
			setBatchFile(null);
			setBatchResult(null);
			setBatchServerResponse(null);
			setNewRunTab("single");
		}
	}, [newRunDialogOpen]);

	useEffect(() => {
		if (!companyId || !featureEnabled) {
			setPlaybooks([]);
			setRuns([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		const unsubscribePlaybooks = onSnapshot(
			outboundPlaybooksCollection(db, companyId),
			(snapshot) => setPlaybooks(snapshot.docs.map((doc) => doc.data())),
			(error) => {
				console.error("Failed to load outbound playbooks", error);
				toast.error(
					t({
						id: "playbookRuns.errors.loadPlaybooksFailed",
						message: "Failed to load outbound playbooks",
					}),
				);
			},
		);
		const unsubscribeRuns = onSnapshot(
			outboundPlaybookRunsCollection(db, companyId),
			(snapshot) => {
				setRuns(
					snapshot.docs
						.map((doc) => doc.data())
						.sort((a, b) => timeValue(b.createdAt) - timeValue(a.createdAt)),
				);
				setLoading(false);
			},
			(error) => {
				console.error("Failed to load outbound playbook runs", error);
				toast.error(
					t({
						id: "playbookRuns.errors.loadRunsFailed",
						message: "Failed to load outbound playbook runs",
					}),
				);
				setLoading(false);
			},
		);

		return () => {
			unsubscribePlaybooks();
			unsubscribeRuns();
		};
	}, [companyId, featureEnabled]);

	useEffect(() => {
		const runIds = runs
			.map((run) => run.id)
			.filter((id): id is string => Boolean(id));
		if (!companyId || runIds.length === 0) {
			setTasks([]);
			setAttempts([]);
			return;
		}

		const taskChunks = chunkValues(runIds);
		const taskMaps = taskChunks.map(() => new Map<string, OutboundTask>());
		const emitTasks = () => {
			const combined = new Map<string, OutboundTask>();
			for (const chunkMap of taskMaps) {
				for (const [id, task] of chunkMap) combined.set(id, task);
			}
			setTasks(Array.from(combined.values()));
		};
		const taskUnsubscribes = taskChunks.map((chunk, index) =>
			onSnapshot(
				query(
					outboundTasksCollection(db, companyId),
					where("playbookRunId", "in", chunk),
				),
				(snapshot) => {
					taskMaps[index] = new Map(
						snapshot.docs.map((doc) => [doc.id, doc.data()]),
					);
					emitTasks();
				},
				(error) => {
					console.error("Failed to load outbound tasks", error);
					toast.error(
						t({
							id: "playbookRuns.errors.loadTasksFailed",
							message: "Failed to load outbound tasks",
						}),
					);
				},
			),
		);

		const attemptMaps = taskChunks.map(
			() => new Map<string, OutboundTaskAttempt>(),
		);
		const emitAttempts = () => {
			const combined = new Map<string, OutboundTaskAttempt>();
			for (const chunkMap of attemptMaps) {
				for (const [id, attempt] of chunkMap) combined.set(id, attempt);
			}
			setAttempts(Array.from(combined.values()));
		};
		const attemptUnsubscribes = taskChunks.map((chunk, index) =>
			onSnapshot(
				query(
					outboundTaskAttemptsCollection(db, companyId),
					where("playbookRunId", "in", chunk),
				),
				(snapshot) => {
					attemptMaps[index] = new Map(
						snapshot.docs.map((doc) => [doc.id, doc.data()]),
					);
					emitAttempts();
				},
				(error) => {
					console.error("Failed to load outbound task attempts", error);
					toast.error(
						t({
							id: "playbookRuns.errors.loadAttemptsFailed",
							message: "Failed to load outbound task attempts",
						}),
					);
				},
			),
		);

		return () => {
			taskUnsubscribes.forEach((unsubscribe) => unsubscribe());
			attemptUnsubscribes.forEach((unsubscribe) => unsubscribe());
		};
	}, [companyId, runs]);

	const playbookById = useMemo(
		() =>
			new Map(
				playbooks
					.filter((playbook) => playbook.id)
					.map((playbook) => [playbook.id!, playbook]),
			),
		[playbooks],
	);
	const agentById = useMemo(
		() => new Map(agents.map((agent) => [agent.id, agent])),
		[agents],
	);
	const tasksByRunId = useMemo(() => groupTasksByRunId(tasks), [tasks]);
	const attemptsByTaskId = useMemo(
		() => groupAttemptsByTaskId(attempts),
		[attempts],
	);
	const enabledPlaybooks = useMemo(
		() =>
			playbooks
				.filter((playbook) => playbook.id && playbook.status === "enabled")
				.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
		[playbooks],
	);
	const newRunPlaybook =
		enabledPlaybooks.find((playbook) => playbook.id === newRunPlaybookId) ||
		null;
	const newRunFields = useMemo(
		() => (newRunPlaybook ? schemaFields(newRunPlaybook.inputSchema) : []),
		[newRunPlaybook],
	);

	useEffect(() => {
		if (!newRunDialogOpen) return;
		const defaultPlaybookId =
			playbookFilter !== "all" &&
			enabledPlaybooks.some((playbook) => playbook.id === playbookFilter)
				? playbookFilter
				: (enabledPlaybooks[0]?.id ?? "");
		setNewRunPlaybookId(defaultPlaybookId);
	}, [enabledPlaybooks, newRunDialogOpen, playbookFilter]);

	useEffect(() => {
		setNewRunValues(initialValues(newRunFields));
	}, [newRunFields]);

	useEffect(() => {
		if (!batchFile || newRunFields.length === 0) {
			setBatchResult(null);
			return;
		}
		let isMounted = true;
		(async () => {
			setBatchParsing(true);
			try {
				const { headers, rows } = await parseSpreadsheetFile(batchFile);
				const result = validateAndMapBatchRows(newRunFields, headers, rows);
				if (isMounted) setBatchResult(result);
			} catch (err) {
				console.error("Failed to parse batch file", err);
			} finally {
				if (isMounted) setBatchParsing(false);
			}
		})();
		return () => {
			isMounted = false;
		};
	}, [newRunFields, batchFile]);

	const handleBatchFileChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file) {
			setBatchFile(null);
			setBatchResult(null);
			setBatchServerResponse(null);
			return;
		}
		setBatchFile(file);
		setBatchResult(null);
		setBatchServerResponse(null);
	};

	const filteredRuns = useMemo(() => {
		const fromTime = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : 0;
		const toTime = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : 0;
		const normalizedQuery = searchQuery.trim().toLowerCase();
		return runs.filter((run) => {
			const playbook = playbookById.get(run.playbookId);
			const agentId = playbook?.callConfiguration.agentId || "";
			const agent = agentId ? agentById.get(agentId) : undefined;
			const created = timeValue(run.createdAt);
			if (playbookFilter !== "all" && run.playbookId !== playbookFilter) {
				return false;
			}
			if (statusFilter !== "all" && run.status !== statusFilter) return false;
			if (fromTime && created < fromTime) return false;
			if (toTime && created > toTime) return false;
			if (!normalizedQuery) return true;
			const haystack = [
				run.id,
				run.origin.externalId,
				run.origin.idempotencyKey,
				runPlaybookName(run, playbook),
				agent?.name,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();
			return haystack.includes(normalizedQuery);
		});
	}, [
		agentById,
		fromDate,
		playbookById,
		playbookFilter,
		runs,
		searchQuery,
		statusFilter,
		toDate,
	]);
	const activeFilterCount = [
		searchQuery.trim(),
		playbookFilter !== "all",
		statusFilter !== "all",
		fromDate,
		toDate,
	].filter(Boolean).length;
	const hasActiveFilters = activeFilterCount > 0;
	const clearFilters = () => {
		setSearchQuery("");
		setPlaybookFilter("all");
		setStatusFilter("all");
		setFromDate("");
		setToDate("");
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Run, filter, and page-size changes intentionally reset pagination without reading their values.
	useEffect(() => {
		setCurrentPage(1);
	}, [
		searchQuery,
		playbookFilter,
		statusFilter,
		fromDate,
		toDate,
		runsPerPage,
	]);

	const totalPages = Math.ceil(filteredRuns.length / runsPerPage);
	const startIndex = (currentPage - 1) * runsPerPage;
	const endIndex = startIndex + runsPerPage;
	const paginatedRuns = useMemo(() => {
		return filteredRuns.slice(startIndex, endIndex);
	}, [filteredRuns, startIndex, endIndex]);

	const selectedRun =
		filteredRuns.find((run) => run.id === selectedRunId) ||
		filteredRuns[0] ||
		null;
	const selectedRunTasks = useMemo(
		() => (selectedRun ? tasksByRunId.get(selectedRun.id || "") || [] : []),
		[selectedRun, tasksByRunId],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Run, filter, and page-size changes intentionally reset pagination without reading their values.
	useEffect(() => {
		setCurrentTasksPage(1);
	}, [selectedRun?.id, tasksPerPage]);

	const totalTasksPages = Math.ceil(selectedRunTasks.length / tasksPerPage);
	const startTasksIndex = (currentTasksPage - 1) * tasksPerPage;
	const endTasksIndex = startTasksIndex + tasksPerPage;
	const paginatedTasks = useMemo(() => {
		return selectedRunTasks.slice(startTasksIndex, endTasksIndex);
	}, [selectedRunTasks, startTasksIndex, endTasksIndex]);

	const selectedRunPlaybook = useMemo(() => {
		if (!selectedRun) return undefined;
		return (
			playbookById.get(selectedRun.playbookId) || selectedRun.playbookSnapshot
		);
	}, [playbookById, selectedRun]);

	const selectedRunInputSchemaFields = useMemo(() => {
		const schema =
			selectedRunPlaybook?.inputSchema ||
			selectedRun?.playbookSnapshot?.inputSchema;
		if (
			!schema ||
			typeof schema !== "object" ||
			!schema.properties ||
			typeof schema.properties !== "object"
		) {
			return [];
		}
		return Object.keys(schema.properties as Record<string, unknown>);
	}, [selectedRunPlaybook, selectedRun]);

	const selectedRunResultSchemaFields = useMemo(() => {
		const schema =
			selectedRunPlaybook?.resultSchema ||
			selectedRun?.playbookSnapshot?.resultSchema;
		if (
			!schema ||
			typeof schema !== "object" ||
			!schema.properties ||
			typeof schema.properties !== "object"
		) {
			return [];
		}
		return Object.keys(schema.properties as Record<string, unknown>);
	}, [selectedRunPlaybook, selectedRun]);

	const selectedRunInputPhoneFields = useMemo(
		() =>
			phoneFieldsFromSchema(
				selectedRunPlaybook?.inputSchema ||
					selectedRun?.playbookSnapshot?.inputSchema,
			),
		[selectedRunPlaybook, selectedRun],
	);

	const selectedRunResultPhoneFields = useMemo(
		() =>
			phoneFieldsFromSchema(
				selectedRunPlaybook?.resultSchema ||
					selectedRun?.playbookSnapshot?.resultSchema,
			),
		[selectedRunPlaybook, selectedRun],
	);

	const selectedRunInputHiddenFields = useMemo(
		() =>
			schemaHiddenFields(
				selectedRunPlaybook?.inputSchema ||
					selectedRun?.playbookSnapshot?.inputSchema,
			),
		[selectedRunPlaybook, selectedRun],
	);

	const selectedRunResultHiddenFields = useMemo(
		() =>
			schemaHiddenFields(
				selectedRunPlaybook?.resultSchema ||
					selectedRun?.playbookSnapshot?.resultSchema,
			),
		[selectedRunPlaybook, selectedRun],
	);

	const handleDownloadCsv = () => {
		if (!selectedRun || selectedRunTasks.length === 0) return;

		const filename = `playbook-run-${selectedRun.id || "export"}-${showAttempts ? "attempts" : "tasks"}-${detailMode}.csv`;

		if (!showAttempts) {
			if (detailMode === "basic") {
				const headers = [
					"Task ID",
					"Attempt Count",
					"Max Attempts",
					"Status",
					"Created At",
					...selectedRunInputSchemaFields.map((f) => `Input: ${f}`),
					...selectedRunResultSchemaFields.map((f) => `Result: ${f}`),
				];
				const rows = selectedRunTasks.map((task) => {
					const attemptsForTask = attemptsByTaskId.get(task.id || "") || [];
					const latestAttempt = attemptsForTask[0];
					const resultPayload =
						task.result || latestAttempt?.structuredResult || {};
					return [
						task.id || "",
						task.attemptCount ?? 0,
						task.maxAttempts ?? 1,
						task.status,
						formatDateTime(task.createdAt),
						...selectedRunInputSchemaFields.map((field) =>
							formatPayloadValue(task.input?.[field]),
						),
						...selectedRunResultSchemaFields.map((field) =>
							formatPayloadValue(resultPayload?.[field]),
						),
					];
				});
				downloadCsv(filename, headers, rows);
			} else {
				const headers = [
					"Task ID",
					"Attempt Count",
					"Max Attempts",
					"Status",
					"Latest Outcome",
					"Latest Summary",
					"Error",
					"Error Code",
					"Inputs (JSON)",
					"Results (JSON)",
					"Additional Fields (JSON)",
					"Created At",
					"Completed At",
				];
				const rows = selectedRunTasks.map((task) => {
					const attemptsForTask = attemptsByTaskId.get(task.id || "") || [];
					const latestAttempt = attemptsForTask[0];
					const resultPayload = (task.result ||
						latestAttempt?.structuredResult ||
						{}) as Record<string, unknown>;
					const additional = resultPayload?.additionalFields ?? {};
					return [
						task.id || "",
						task.attemptCount ?? 0,
						task.maxAttempts ?? 1,
						task.status,
						task.latestOutcome || latestAttempt?.outcome || "",
						latestAttempt?.summary || "",
						task.error || latestAttempt?.error || "",
						task.errorCode || latestAttempt?.errorCode || "",
						JSON.stringify(task.input || {}),
						JSON.stringify(resultPayload),
						JSON.stringify(additional),
						formatDateTime(task.createdAt),
						formatDateTime(task.completedAt),
					];
				});
				downloadCsv(filename, headers, rows);
			}
		} else {
			if (detailMode === "basic") {
				const headers = [
					"Task ID",
					"Attempt ID",
					"Attempt Number",
					"Attempt Status",
					"Started/Completed At",
					...selectedRunInputSchemaFields.map((f) => `Input: ${f}`),
					...selectedRunResultSchemaFields.map((f) => `Result: ${f}`),
				];
				const rows: Array<Array<string | number>> = [];
				for (const task of selectedRunTasks) {
					const attemptsForTask = attemptsByTaskId.get(task.id || "") || [];
					if (attemptsForTask.length === 0) {
						rows.push([
							task.id || "",
							"",
							"0",
							task.status,
							formatDateTime(task.createdAt),
							...selectedRunInputSchemaFields.map((field) =>
								formatPayloadValue(task.input?.[field]),
							),
							...selectedRunResultSchemaFields.map(() => "-"),
						]);
					} else {
						for (const attempt of attemptsForTask) {
							const resultPayload =
								attempt.structuredResult || task.result || {};
							rows.push([
								task.id || "",
								attempt.id || "",
								String(attempt.attemptNumber ?? 1),
								attempt.status,
								formatDateTime(latestAttemptTime(attempt)),
								...selectedRunInputSchemaFields.map((field) =>
									formatPayloadValue(task.input?.[field]),
								),
								...selectedRunResultSchemaFields.map((field) =>
									formatPayloadValue(resultPayload?.[field]),
								),
							]);
						}
					}
				}
				downloadCsv(filename, headers, rows);
			} else {
				const headers = [
					"Task ID",
					"Attempt ID",
					"Attempt Number",
					"Attempt Status",
					"Worker ID",
					"Provider",
					"Provider Call SID",
					"Outcome",
					"Summary",
					"Error",
					"Error Code",
					"Inputs (JSON)",
					"Results (JSON)",
					"Additional Fields (JSON)",
					"Created At",
					"Completed At",
				];
				const rows: Array<Array<string | number>> = [];
				for (const task of selectedRunTasks) {
					const attemptsForTask = attemptsByTaskId.get(task.id || "") || [];
					if (attemptsForTask.length === 0) {
						rows.push([
							task.id || "",
							"",
							"0",
							task.status,
							"",
							"",
							"",
							task.latestOutcome || "",
							"",
							task.error || "",
							task.errorCode || "",
							JSON.stringify(task.input || {}),
							JSON.stringify(task.result || {}),
							"{}",
							formatDateTime(task.createdAt),
							formatDateTime(task.completedAt),
						]);
					} else {
						for (const attempt of attemptsForTask) {
							const resultPayload = (attempt.structuredResult ||
								task.result ||
								{}) as Record<string, unknown>;
							const additional = resultPayload?.additionalFields ?? {};
							rows.push([
								task.id || "",
								attempt.id || "",
								String(attempt.attemptNumber ?? 1),
								attempt.status,
								attempt.workerId || "",
								attempt.provider || "",
								attempt.providerCallSid || "",
								attempt.outcome || "",
								attempt.summary || "",
								attempt.error || "",
								attempt.errorCode || "",
								JSON.stringify(task.input || {}),
								JSON.stringify(resultPayload),
								JSON.stringify(additional),
								formatDateTime(attempt.createdAt),
								formatDateTime(attempt.completedAt),
							]);
						}
					}
				}
				downloadCsv(filename, headers, rows);
			}
		}
	};

	const handleNewRunFieldChange = (
		field: SchemaField,
		value: string | boolean,
	) => {
		setNewRunValues((current) => ({
			...current,
			[field.name]: value,
		}));
	};

	const handleSubmitNewRun = async (event: FormEvent) => {
		event.preventDefault();
		if (!companyId || !newRunPlaybook?.id) return;

		setSubmittingRun(true);
		setBatchServerResponse(null);

		if (newRunTab === "batch") {
			if (
				batchParsing ||
				!batchResult ||
				!batchResult.valid ||
				batchResult.taskInputs.length === 0
			) {
				setSubmittingRun(false);
				return;
			}
			try {
				const key = idempotencyKey();
				const result = await createOutboundPlaybookRunForCompany({
					companyId,
					playbookId: newRunPlaybook.id,
					taskInputs: batchResult.taskInputs,
					idempotencyKey: key,
					externalId: key,
					scheduleImmediately,
				});
				setBatchServerResponse(result.data);
				if (result.data.runId) {
					setSelectedRunId(result.data.runId);
				}
				if (result.data.success) {
					const createdCount =
						result.data.createdCount ?? result.data.taskIds.length;
					const failedCount = result.data.failedCount ?? 0;
					toast.success(
						i18n._({
							id: "playbookRuns.newRun.toasts.batchReceived",
							message:
								"Playbook run received and scheduled ({createdCount} created, {failedCount} failed).",
							values: { createdCount, failedCount },
						}),
					);
					setNewRunDialogOpen(false);
				}
			} catch (error) {
				console.error("Failed to create batch outbound playbook run", error);
				const message =
					error instanceof Error
						? error.message
						: t({
								id: "playbookRuns.newRun.errors.createFailed",
								message: "Failed to submit playbook run.",
							});
				toast.error(message);
			} finally {
				setSubmittingRun(false);
			}
			return;
		}

		try {
			const input = buildInputPayload(newRunFields, newRunValues);
			const key = idempotencyKey();
			const result = await createOutboundPlaybookRunForCompany({
				companyId,
				playbookId: newRunPlaybook.id,
				input,
				idempotencyKey: key,
				externalId: key,
				scheduleImmediately,
			});
			setSelectedRunId(result.data.runId);
			setNewRunDialogOpen(false);
			toast.success(
				t({
					id: "playbookRuns.newRun.toasts.created",
					message: "Playbook run submitted.",
				}),
			);
		} catch (error) {
			console.error("Failed to create outbound playbook run", error);
			const message =
				error instanceof Error
					? error.message
					: t({
							id: "playbookRuns.newRun.errors.createFailed",
							message: "Failed to submit playbook run.",
						});
			toast.error(message);
		} finally {
			setSubmittingRun(false);
		}
	};

	const handleRetryTask = async (task: OutboundTask) => {
		if (!companyId || !task.id) return;
		setRetryingTaskIds((current) => new Set(current).add(task.id!));
		try {
			const result = await forceRetryOutboundTaskForCompany({
				companyId,
				taskId: task.id,
			});
			toast.success(
				i18n._({
					id: "playbookRuns.retry.toasts.scheduled",
					message: "Retry attempt {attemptNumber} scheduled.",
					values: { attemptNumber: result.data.attemptNumber },
				}),
			);
		} catch (error) {
			console.error("Failed to retry outbound task", error);
			toast.error(
				error instanceof Error
					? error.message
					: t({
							id: "playbookRuns.retry.errors.failed",
							message: "Failed to schedule retry attempt.",
						}),
			);
		} finally {
			setRetryingTaskIds((current) => {
				const next = new Set(current);
				next.delete(task.id!);
				return next;
			});
		}
	};

	const handlePauseRun = async (run: OutboundPlaybookRun) => {
		if (!companyId || !run.id) return;
		setPausingRunIds((current) => new Set(current).add(run.id!));
		try {
			await pauseOutboundPlaybookRunForCompany({
				companyId,
				playbookRunId: run.id,
			});
			toast.success(
				t({
					id: "playbookRuns.control.toasts.paused",
					message: "Playbook run paused.",
				}),
			);
		} catch (error) {
			console.error("Failed to pause playbook run", error);
			toast.error(
				error instanceof Error
					? error.message
					: t({
							id: "playbookRuns.control.errors.pauseFailed",
							message: "Failed to pause playbook run.",
						}),
			);
		} finally {
			setPausingRunIds((current) => {
				const next = new Set(current);
				next.delete(run.id!);
				return next;
			});
		}
	};

	const handleResumeRun = async (run: OutboundPlaybookRun) => {
		if (!companyId || !run.id) return;
		setResumingRunIds((current) => new Set(current).add(run.id!));
		try {
			await resumeOutboundPlaybookRunForCompany({
				companyId,
				playbookRunId: run.id,
			});
			toast.success(
				t({
					id: "playbookRuns.control.toasts.resumed",
					message: "Playbook run resumed.",
				}),
			);
		} catch (error) {
			console.error("Failed to resume playbook run", error);
			toast.error(
				error instanceof Error
					? error.message
					: t({
							id: "playbookRuns.control.errors.resumeFailed",
							message: "Failed to resume playbook run.",
						}),
			);
		} finally {
			setResumingRunIds((current) => {
				const next = new Set(current);
				next.delete(run.id!);
				return next;
			});
		}
	};

	const handleCancelRun = async (run: OutboundPlaybookRun) => {
		if (!companyId || !run.id) return;
		setCancellingRunIds((current) => new Set(current).add(run.id!));
		try {
			await cancelOutboundPlaybookRunForCompany({
				companyId,
				playbookRunId: run.id,
			});
			toast.success(
				t({
					id: "playbookRuns.control.toasts.cancelled",
					message: "Playbook run cancelled.",
				}),
			);
		} catch (error) {
			console.error("Failed to cancel playbook run", error);
			toast.error(
				error instanceof Error
					? error.message
					: t({
							id: "playbookRuns.control.errors.cancelFailed",
							message: "Failed to cancel playbook run.",
						}),
			);
		} finally {
			setCancellingRunIds((current) => {
				const next = new Set(current);
				next.delete(run.id!);
				return next;
			});
		}
	};

	const renderKeyValuePayload = (
		payload: OutboundPayload | null | undefined,
		emptyMessage = t({
			id: "playbookRuns.payload.empty",
			message: "No values",
		}),
		phoneFields?: Set<string>,
	) => {
		const entries = payloadEntries(payload);
		if (entries.length === 0) {
			return <span className="text-slate-500">{emptyMessage}</span>;
		}

		return (
			<dl className="grid w-full min-w-0 grid-cols-[minmax(0,0.4fr)_minmax(0,0.6fr)] gap-x-3 gap-y-1 text-xs md:grid-cols-[10rem_minmax(0,1fr)] [&>*]:min-w-0 [&>*]:[overflow-wrap:anywhere]">
				{entries.map(([key, value]) => {
					const isPhone = phoneFields?.has(key) || isPhoneKey(key);
					return (
						<Fragment key={key}>
							<dt className="self-start break-words font-medium text-slate-600">
								{key}
							</dt>
							<dd className="self-start whitespace-normal break-words text-slate-900">
								{isPhone ? (
									<span dir="ltr" className="inline-block">
										{formatPayloadValue(value, key)}
									</span>
								) : (
									formatPayloadValue(value, key)
								)}
							</dd>
						</Fragment>
					);
				})}
			</dl>
		);
	};

	const renderDateTimeParts = (value: unknown) => {
		const parts = formatDateTimeParts(value);
		if (!parts) return "-";
		return (
			<div className="whitespace-nowrap text-xs">
				<div>{parts.date}</div>
				<div className="text-slate-500">{parts.time}</div>
			</div>
		);
	};

	const renderTaskActionsMenu = ({
		task,
		retrying,
		retryDisabled,
	}: {
		task: OutboundTask;
		taskId: string;
		taskAttempts: OutboundTaskAttempt[];
		attemptsExpanded: boolean;
		retrying: boolean;
		retryDisabled: boolean;
	}) => (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-11 w-11 p-0 md:h-8 md:w-8"
					aria-label={t({
						id: "playbookRuns.tasksTable.actions",
						message: "Actions",
					})}
				>
					<MoreHorizontal className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuItem
					disabled={retryDisabled}
					onClick={() => handleRetryTask(task)}
				>
					{retrying ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<RotateCcw className="h-4 w-4" />
					)}
					{t({
						id: "playbookRuns.retry.action",
						message: "Retry",
					})}
				</DropdownMenuItem>
				<DropdownMenuItem
					onClick={() =>
						setTechnicalDetails({
							title: t({
								id: "playbookRuns.technicalDetails.taskTitle",
								message: "Task document",
							}),
							document: task,
						})
					}
				>
					<FileJson className="h-4 w-4" />
					{t({
						id: "playbookRuns.technicalDetails.action",
						message: "Show technical details",
					})}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);

	const renderRunActions = (run: OutboundPlaybookRun) => {
		const canPause =
			run.status === "pending" ||
			run.status === "scheduled" ||
			run.status === "running";
		const canResume = run.status === "paused";
		const canCancel =
			run.status === "pending" ||
			run.status === "scheduled" ||
			run.status === "running" ||
			run.status === "paused";
		const pausing = run.id ? pausingRunIds.has(run.id) : false;
		const resuming = run.id ? resumingRunIds.has(run.id) : false;
		const cancelling = run.id ? cancellingRunIds.has(run.id) : false;

		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-11 w-11 p-0 md:h-8 md:w-8"
						aria-label={t({
							id: "playbookRuns.runActions.menu",
							message: "Run actions",
						})}
						onClick={(event) => event.stopPropagation()}
					>
						<MoreHorizontal className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					{canPause && (
						<DropdownMenuItem
							disabled={pausing}
							onClick={(event) => {
								event.stopPropagation();
								handlePauseRun(run);
							}}
						>
							{pausing ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Pause className="mr-2 h-4 w-4" />
							)}
							{t({
								id: "playbookRuns.actions.pause",
								message: "Pause Run",
							})}
						</DropdownMenuItem>
					)}
					{canResume && (
						<DropdownMenuItem
							disabled={resuming}
							onClick={(event) => {
								event.stopPropagation();
								handleResumeRun(run);
							}}
						>
							{resuming ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Play className="mr-2 h-4 w-4" />
							)}
							{t({
								id: "playbookRuns.actions.resume",
								message: "Resume Run",
							})}
						</DropdownMenuItem>
					)}
					{canCancel && (
						<DropdownMenuItem
							disabled={cancelling}
							className="text-danger-ink focus:text-danger-ink"
							onClick={(event) => {
								event.stopPropagation();
								handleCancelRun(run);
							}}
						>
							{cancelling ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Square className="mr-2 h-4 w-4" />
							)}
							{t({
								id: "playbookRuns.actions.cancel",
								message: "Cancel Run",
							})}
						</DropdownMenuItem>
					)}
					<DropdownMenuItem
						onClick={(event) => {
							event.stopPropagation();
							setTechnicalDetails({
								title: t({
									id: "playbookRuns.technicalDetails.runTitle",
									message: "Run document",
								}),
								document: run,
							});
						}}
					>
						<FileJson className="mr-2 h-4 w-4" />
						{t({
							id: "playbookRuns.technicalDetails.action",
							message: "Show technical details",
						})}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	const renderRecoveryReview = (task: OutboundTask) =>
		task.recoveryReview ? (
			<div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
				<Badge
					variant="outline"
					className="border-amber-500/50 bg-amber-50 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
				>
					{t({
						id: "playbookRuns.recoveryReview.badge",
						message: "Needs review",
					})}
				</Badge>
				<span className="text-muted-foreground">
					{recoveryReviewLabel(task.recoveryReview.reason)}
				</span>
			</div>
		) : null;

	const renderAttempt = (task: OutboundTask, attempt: OutboundTaskAttempt) => {
		const dateTime = formatDateTimeParts(latestAttemptTime(attempt));
		return (
			<div
				key={attempt.id}
				className="rounded-lg border-[1.5px] border-card-border bg-card/90 p-3 shadow-card dark:bg-slate-900/90"
			>
				<div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2 dark:border-slate-800">
					<div className="flex min-w-0 flex-wrap items-center gap-2 text-xs">
						<span className="font-semibold text-slate-800 dark:text-slate-200">
							#{attempt.attemptNumber}
						</span>
						<Badge
							variant="outline"
							className={statusBadgeClass(attempt.status)}
						>
							{statusLabel(attempt.status)}
						</Badge>
						{dateTime && (
							<span className="text-slate-500">
								{dateTime.date} {dateTime.time}
							</span>
						)}
					</div>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-7 w-7 p-0"
						aria-label={t({
							id: "playbookRuns.technicalDetails.action",
							message: "Show technical details",
						})}
						onClick={() =>
							setTechnicalDetails({
								title: t({
									id: "playbookRuns.technicalDetails.attemptTitle",
									message: "Attempt document",
								}),
								document: attempt,
							})
						}
					>
						<FileJson className="h-4 w-4" />
					</Button>
				</div>
				<div className="grid gap-3 pt-2.5 md:grid-cols-2">
					<div>
						<div className="mb-1 text-[11px] font-semibold text-slate-500">
							{t({
								id: "playbookRuns.attemptsTable.inputs",
								message: "Inputs",
							})}
						</div>
						{renderKeyValuePayload(
							filterPayloadForMode(
								task.input,
								detailMode,
								selectedRunInputSchemaFields,
								selectedRunInputHiddenFields,
							),
							undefined,
							selectedRunInputPhoneFields,
						)}
					</div>
					<div>
						<div className="mb-1 text-[11px] font-semibold text-slate-500">
							{t({
								id: "playbookRuns.attemptsTable.result",
								message: "Results",
							})}
						</div>
						{renderKeyValuePayload(
							filterPayloadForMode(
								attempt.structuredResult,
								detailMode,
								selectedRunResultSchemaFields,
								selectedRunResultHiddenFields,
							),
							undefined,
							selectedRunResultPhoneFields,
						)}
					</div>
				</div>
			</div>
		);
	};

	if (!featureEnabled) return null;

	return (
		<div className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
			<PageHeader
				className="sm:flex-row sm:items-end sm:justify-between"
				title={t({ id: "playbookRuns.title", message: "Playbook Runs" })}
				description={t({
					id: "playbookRuns.description",
					message:
						"Review outbound playbook execution, task status, and call results.",
				})}
				actions={
					<Button
						className="w-full sm:w-auto"
						onClick={() => setNewRunDialogOpen(true)}
						disabled={enabledPlaybooks.length === 0}
					>
						<Plus className="me-2 h-4 w-4" />
						{t({ id: "playbookRuns.newRun.action", message: "New Run" })}
					</Button>
				}
			/>

			<Card>
				<CardHeader className="gap-1 border-b border-card-border">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<CardTitle className="text-lg font-bold">
							{t({ id: "playbookRuns.runsCard.title", message: "Runs" })}
						</CardTitle>
						<p className="text-sm tabular-nums text-muted-foreground">
							{hasActiveFilters
								? i18n._("playbookRuns.filteredCount", {
										visible: filteredRuns.length,
										total: runs.length,
									})
								: i18n._("playbookRuns.totalCount", { count: runs.length })}
						</p>
					</div>
					<CardDescription>
						{t({
							id: "playbookRuns.runsCard.description",
							message: "Choose a run to review its calls and results.",
						})}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid min-w-0 gap-3">
						<div className="relative min-w-0 w-full">
							<Search className="pointer-events-none absolute start-3 top-2.5 h-4 w-4 text-muted-foreground" />
							<Input
								aria-label={t({
									id: "playbookRuns.filters.searchLabel",
									message: "Search playbook runs",
								})}
								placeholder={t({
									id: "playbookRuns.filters.searchPlaceholder",
									message: "Search run, playbook, agent, or external ID",
								})}
								value={searchQuery}
								onChange={(event) => setSearchQuery(event.target.value)}
								{...getContentFieldProps(
									searchQuery,
									language === "he" ? "rtl" : "ltr",
									"ps-9",
								)}
							/>
						</div>
						<div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-2">
							<Select value={playbookFilter} onValueChange={setPlaybookFilter}>
								<SelectTrigger className="w-full">
									<SelectValue
										placeholder={t({
											id: "playbookRuns.filters.playbookPlaceholder",
											message: "Playbook",
										})}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">
										{t({
											id: "playbookRuns.filters.allPlaybooks",
											message: "All playbooks",
										})}
									</SelectItem>
									{playbooks
										.filter((playbook) => playbook.id)
										.map((playbook) => (
											<SelectItem key={playbook.id} value={playbook.id!}>
												{playbook.name || playbook.id}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
							<Select
								value={statusFilter}
								onValueChange={(value: unknown) =>
									setStatusFilter(value as RunStatusFilter)
								}
							>
								<SelectTrigger className="w-full">
									<SelectValue
										placeholder={t({
											id: "playbookRuns.filters.statusPlaceholder",
											message: "Status",
										})}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">
										{t({
											id: "playbookRuns.filters.allStatuses",
											message: "All statuses",
										})}
									</SelectItem>
									<SelectItem value="pending">
										{t({
											id: "playbookRuns.status.pending",
											message: "Pending",
										})}
									</SelectItem>
									<SelectItem value="scheduled">
										{t({
											id: "playbookRuns.status.scheduled",
											message: "Scheduled",
										})}
									</SelectItem>
									<SelectItem value="running">
										{t({
											id: "playbookRuns.status.running",
											message: "Running",
										})}
									</SelectItem>
									<SelectItem value="paused">
										{t({ id: "playbookRuns.status.paused", message: "Paused" })}
									</SelectItem>
									<SelectItem value="completed">
										{t({
											id: "playbookRuns.status.completed",
											message: "Completed",
										})}
									</SelectItem>
									<SelectItem value="partially_failed">
										{t({
											id: "playbookRuns.status.partiallyFailed",
											message: "Partially failed",
										})}
									</SelectItem>
									<SelectItem value="failed">
										{t({ id: "playbookRuns.status.failed", message: "Failed" })}
									</SelectItem>
									<SelectItem value="cancelled">
										{t({
											id: "playbookRuns.status.cancelled",
											message: "Cancelled",
										})}
									</SelectItem>
								</SelectContent>
							</Select>
							<DateRangeFilter
								language={language}
								dateRange={{
									from: fromDate ? new Date(`${fromDate}T00:00:00`) : undefined,
									to: toDate ? new Date(`${toDate}T00:00:00`) : undefined,
								}}
								onChange={(range) => {
									setFromDate(
										range.from ? format(range.from, "yyyy-MM-dd") : "",
									);
									setToDate(range.to ? format(range.to, "yyyy-MM-dd") : "");
								}}
							/>
						</div>
					</div>

					{isMobile ? (
						<div className="space-y-3">
							{loading ? (
								<p role="status">
									{t({
										id: "playbookRuns.loading",
										message: "Loading playbook runs...",
									})}
								</p>
							) : filteredRuns.length === 0 ? (
								<div className="space-y-3 py-6 text-center">
									<p>
										{hasActiveFilters
											? t({
													id: "playbookRuns.emptyFiltered",
													message: "No runs match these filters.",
												})
											: t({
													id: "playbookRuns.empty",
													message: "No playbook runs yet.",
												})}
									</p>
									{hasActiveFilters && (
										<Button variant="outline" onClick={clearFilters}>
											{t({
												id: "playbookRuns.filters.clear",
												message: "Clear filters",
											})}
										</Button>
									)}
								</div>
							) : (
								paginatedRuns.map((run) => {
									const playbook = playbookById.get(run.playbookId);
									const agent = playbook?.callConfiguration.agentId
										? agentById.get(playbook.callConfiguration.agentId)
										: undefined;
									const selected = selectedRun?.id === run.id;
									const finished = terminalTaskCount(run);
									const total = run.taskCounts.total;
									const progress =
										total > 0
											? Math.min(100, Math.max(0, (finished / total) * 100))
											: 0;
									const label = i18n._({
										id: "playbookRuns.progressTasks",
										message: "{finished} / {total} tasks",
										values: { finished, total },
									});
									return (
										<article
											key={run.id}
											className={`min-w-0 overflow-hidden rounded-lg border-[1.5px] border-card-border shadow-card ${selected ? "bg-accent/50" : "bg-card"}`}
										>
											<button
												type="button"
												aria-pressed={selected}
												aria-controls="playbook-run-tasks"
												className="block w-full space-y-3 p-4 text-start focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2"
												onClick={() => setSelectedRunId(run.id || null)}
											>
												<div className="flex items-start justify-between gap-3">
													<div className="min-w-0 flex-1">
														<div className="flex items-start gap-2">
															{selected && (
																<CheckCircle2
																	aria-label={t({
																		id: "playbookRuns.selected",
																		message: "Selected run",
																	})}
																	className="mt-0.5 h-4 w-4 shrink-0 text-accent-ink"
																/>
															)}
															<span className="font-semibold [overflow-wrap:anywhere]">
																{runPlaybookName(run, playbook)}
															</span>
														</div>
														<div className="mt-1 text-xs text-muted-foreground [overflow-wrap:anywhere]">
															{agent?.name || "—"}
														</div>
													</div>
													<Badge
														variant="outline"
														className={`${statusBadgeClass(run.status)} max-w-[45%] whitespace-normal`}
													>
														{statusLabel(run.status)}
													</Badge>
												</div>
												<div className="flex items-center gap-4">
													<div className="min-w-0 flex-1 space-y-1.5">
														<div
															role="progressbar"
															aria-label={t({
																id: "playbookRuns.table.progress",
																message: "Progress",
															})}
															aria-valuemin={0}
															aria-valuemax={100}
															aria-valuenow={Math.round(progress)}
															aria-valuetext={label}
															className="h-1.5 overflow-hidden rounded-full bg-muted"
														>
															<div
																className="h-full rounded-full bg-primary"
																style={{ width: `${progress}%` }}
															/>
														</div>
														<div className="text-xs tabular-nums">{label}</div>
													</div>
													<div className="text-end">
														<div className="font-semibold tabular-nums">
															{finished ? runSuccessRate(run) : "—"}
														</div>
														<div className="text-xs text-muted-foreground">
															{t({
																id: "playbookRuns.table.success",
																message: "Success",
															})}
														</div>
													</div>
												</div>
											</button>
											<div className="flex items-center justify-between gap-2 border-t border-card-border px-4 py-2">
												<div className="min-w-0 space-y-1 text-xs text-muted-foreground">
													<div>{formatDateTime(run.createdAt)}</div>
													<div>
														{t({
															id: "playbookRuns.table.duration",
															message: "Time to Complete",
														})}
														:{" "}
														{formatDuration(
															run.startedAt || run.createdAt,
															run.completedAt ||
																run.cancelledAt ||
																run.lastTaskStateChangedAt,
														)}
													</div>
												</div>
												{renderRunActions(run)}
											</div>
										</article>
									);
								})
							)}
						</div>
					) : (
						<Table className="min-w-[800px] table-fixed [&_th]:text-start [&_th]:whitespace-normal [&_td]:py-4">
							<TableHeader>
								<TableRow>
									<TableHead className="w-[24%]">
										{t({ id: "playbookRuns.table.run", message: "Run" })}
									</TableHead>

									<TableHead className="w-[15%]">
										{t({ id: "playbookRuns.table.status", message: "Status" })}
									</TableHead>
									<TableHead className="w-[17%]">
										{t({
											id: "playbookRuns.table.progress",
											message: "Progress",
										})}
									</TableHead>
									<TableHead className="w-[9%]">
										{t({
											id: "playbookRuns.table.success",
											message: "Success",
										})}
									</TableHead>
									<TableHead className="w-[17%]">
										{t({
											id: "playbookRuns.table.created",
											message: "Created",
										})}
									</TableHead>
									<TableHead className="w-[12%]">
										{t({
											id: "playbookRuns.table.duration",
											message: "Time to Complete",
										})}
									</TableHead>
									<TableHead className="w-12" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={7}>
											{t({
												id: "playbookRuns.loading",
												message: "Loading playbook runs...",
											})}
										</TableCell>
									</TableRow>
								) : filteredRuns.length === 0 ? (
									<TableRow>
										<TableCell colSpan={7}>
											<div className="flex flex-col items-center gap-3 py-8 text-center">
												<p className="font-medium text-foreground">
													{hasActiveFilters
														? t({
																id: "playbookRuns.emptyFiltered",
																message: "No runs match these filters.",
															})
														: t({
																id: "playbookRuns.empty",
																message: "No playbook runs yet.",
															})}
												</p>
												{hasActiveFilters && (
													<Button
														type="button"
														variant="outline"
														size="sm"
														onClick={clearFilters}
													>
														{t({
															id: "playbookRuns.filters.clear",
															message: "Clear filters",
														})}
													</Button>
												)}
											</div>
										</TableCell>
									</TableRow>
								) : (
									paginatedRuns.map((run) => {
										const playbook = playbookById.get(run.playbookId);
										const agent = playbook?.callConfiguration.agentId
											? agentById.get(playbook.callConfiguration.agentId)
											: undefined;
										const isSelectedRun = selectedRun?.id === run.id;
										const finished = terminalTaskCount(run);
										const total = run.taskCounts.total;
										const progress =
											total > 0
												? Math.min(100, Math.max(0, (finished / total) * 100))
												: 0;
										const progressLabel = i18n._({
											id: "playbookRuns.progressTasks",
											message: "{finished} / {total} tasks",
											values: { finished, total },
										});
										return (
											<TableRow
												key={run.id}
												tabIndex={0}
												aria-selected={isSelectedRun}
												className={
													isSelectedRun
														? "cursor-pointer bg-accent/50 hover:bg-accent/70 focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2"
														: "cursor-pointer hover:bg-muted/50 focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2"
												}
												onClick={() => setSelectedRunId(run.id || null)}
												onKeyDown={(event) => {
													if (
														event.target === event.currentTarget &&
														(event.key === "Enter" || event.key === " ")
													) {
														event.preventDefault();
														setSelectedRunId(run.id || null);
													}
												}}
											>
												<TableCell>
													<div className="flex min-w-0 items-center gap-2">
														<span className="w-4 shrink-0">
															{isSelectedRun && (
																<CheckCircle2
																	className="h-4 w-4 text-accent-ink"
																	aria-label={t({
																		id: "playbookRuns.selected",
																		message: "Selected run",
																	})}
																/>
															)}
														</span>
														<div className="min-w-0">
															<div
																className="truncate font-semibold"
																title={runPlaybookName(run, playbook)}
															>
																{runPlaybookName(run, playbook)}
															</div>
															<div
																className="mt-1 truncate text-xs text-muted-foreground"
																title={agent?.name}
															>
																{agent?.name || "—"}
															</div>
														</div>
													</div>
												</TableCell>
												<TableCell>
													<Badge
														variant="outline"
														className={`${statusBadgeClass(run.status)} max-w-full whitespace-normal`}
													>
														{statusLabel(run.status)}
													</Badge>
												</TableCell>
												<TableCell>
													<div className="space-y-1.5">
														<div
															className="h-1.5 overflow-hidden rounded-full bg-muted"
															role="progressbar"
															aria-label={t({
																id: "playbookRuns.table.progress",
																message: "Progress",
															})}
															aria-valuemin={0}
															aria-valuemax={100}
															aria-valuenow={Math.round(progress)}
															aria-valuetext={progressLabel}
														>
															<div
																className="h-full rounded-full bg-primary"
																style={{ width: `${progress}%` }}
															/>
														</div>
														<div className="text-xs tabular-nums text-muted-foreground">
															{progressLabel}
														</div>
													</div>
												</TableCell>
												<TableCell className="font-semibold tabular-nums">
													{finished > 0 ? runSuccessRate(run) : "—"}
												</TableCell>
												<TableCell className="text-xs tabular-nums text-muted-foreground">
													{formatDateTime(run.createdAt)}
												</TableCell>
												<TableCell className="text-xs tabular-nums text-muted-foreground">
													{formatDuration(
														run.startedAt || run.createdAt,
														run.completedAt ||
															run.cancelledAt ||
															run.lastTaskStateChangedAt,
													)}
												</TableCell>
												<TableCell className="w-12">
													{renderRunActions(run)}
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					)}

					<div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
						<div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
							<span>
								{t({
									id: "playbookRuns.pagination.runsPerPage",
									message: "Runs per page:",
								})}
							</span>
							<Select
								value={String(runsPerPage)}
								onValueChange={(val) => {
									setRunsPerPage(Number(val));
									setCurrentPage(1);
								}}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="5">5</SelectItem>
									<SelectItem value="10">10</SelectItem>
									<SelectItem value="25">25</SelectItem>
									<SelectItem value="50">50</SelectItem>
									<SelectItem value="100">100</SelectItem>
								</SelectContent>
							</Select>
							{filteredRuns.length > 0 && (
								<span className="ml-2 text-xs text-slate-500">
									{t({ id: "showing", message: "Showing" })} {startIndex + 1}–
									{Math.min(endIndex, filteredRuns.length)}{" "}
									{t({ id: "of", message: "of" })} {filteredRuns.length}
								</span>
							)}
						</div>

						{totalPages > 1 && (
							<Pagination className="mx-0 w-auto">
								<PaginationContent>
									<PaginationItem>
										<PaginationPrevious
											onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
											className={
												currentPage === 1
													? "pointer-events-none opacity-50"
													: "cursor-pointer"
											}
										/>
									</PaginationItem>

									{Array.from({ length: totalPages }, (_, i) => i + 1)
										.filter((page) => {
											if (page === 1 || page === totalPages) return true;
											if (Math.abs(page - currentPage) <= 1) return true;
											return false;
										})
										.map((page, index, array) => (
											<Fragment key={page}>
												{index > 0 && array[index - 1] !== page - 1 && (
													<PaginationItem key={`ellipsis-${page}`}>
														<PaginationEllipsis />
													</PaginationItem>
												)}
												<PaginationItem>
													<PaginationLink
														onClick={() => setCurrentPage(page)}
														isActive={currentPage === page}
														className="cursor-pointer"
													>
														{page}
													</PaginationLink>
												</PaginationItem>
											</Fragment>
										))}

									<PaginationItem>
										<PaginationNext
											onClick={() =>
												setCurrentPage((p) => Math.min(totalPages, p + 1))
											}
											className={
												currentPage === totalPages
													? "pointer-events-none opacity-50"
													: "cursor-pointer"
											}
										/>
									</PaginationItem>
								</PaginationContent>
							</Pagination>
						)}
					</div>
				</CardContent>
			</Card>

			<Card id="playbook-run-tasks">
				<CardHeader className="flex flex-col gap-4 border-b border-card-border sm:flex-row sm:items-center sm:justify-between">
					<div>
						<CardTitle className="flex items-center gap-2 text-lg font-bold">
							<ListChecks className="h-5 w-5" />
							{t({ id: "playbookRuns.tasksCard.title", message: "Run Tasks" })}
						</CardTitle>
						<CardDescription>
							{selectedRun
								? `${runPlaybookName(selectedRun, playbookById.get(selectedRun.playbookId))} - ${formatDateTime(selectedRun.createdAt)}`
								: t({
										id: "playbookRuns.tasksCard.selectRun",
										message: "Select a run to inspect task details.",
									})}
						</CardDescription>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						{selectedRun && (
							<>
								{(selectedRun.status === "pending" ||
									selectedRun.status === "scheduled" ||
									selectedRun.status === "running") && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={
											selectedRun.id ? pausingRunIds.has(selectedRun.id) : false
										}
										onClick={() => handlePauseRun(selectedRun)}
										className="h-8 text-xs gap-1.5"
									>
										{selectedRun.id && pausingRunIds.has(selectedRun.id) ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<Pause className="h-3.5 w-3.5" />
										)}
										{t({
											id: "playbookRuns.actions.pause",
											message: "Pause Run",
										})}
									</Button>
								)}
								{selectedRun.status === "paused" && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={
											selectedRun.id
												? resumingRunIds.has(selectedRun.id)
												: false
										}
										onClick={() => handleResumeRun(selectedRun)}
										className="h-8 text-xs gap-1.5"
									>
										{selectedRun.id && resumingRunIds.has(selectedRun.id) ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<Play className="h-3.5 w-3.5" />
										)}
										{t({
											id: "playbookRuns.actions.resume",
											message: "Resume Run",
										})}
									</Button>
								)}
								{(selectedRun.status === "pending" ||
									selectedRun.status === "scheduled" ||
									selectedRun.status === "running" ||
									selectedRun.status === "paused") && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={
											selectedRun.id
												? cancellingRunIds.has(selectedRun.id)
												: false
										}
										onClick={() => handleCancelRun(selectedRun)}
										className="h-8 text-xs gap-1.5 text-danger-ink hover:text-danger-ink hover:bg-danger-tint dark:text-danger-ink dark:hover:bg-danger-tint/80"
									>
										{selectedRun.id && cancellingRunIds.has(selectedRun.id) ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<Square className="h-3.5 w-3.5" />
										)}
										{t({
											id: "playbookRuns.actions.cancel",
											message: "Cancel Run",
										})}
									</Button>
								)}
							</>
						)}
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => {
								setShowAttempts((prev) => !prev);
								setMobileAttempts({});
							}}
							className="h-8 text-xs gap-1.5"
						>
							{showAttempts ? (
								<>
									<EyeOff className="h-3.5 w-3.5" />
									{t({
										id: "playbookRuns.toggles.hideAttempts",
										message: "Hide Attempts",
									})}
								</>
							) : (
								<>
									<Eye className="h-3.5 w-3.5" />
									{t({
										id: "playbookRuns.toggles.showAttempts",
										message: "Show Attempts",
									})}
								</>
							)}
						</Button>

						<div className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-slate-100 p-0.5 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-400">
							<button
								type="button"
								onClick={() => setDetailMode("basic")}
								className={`rounded-xs px-2 py-1 font-medium transition-colors ${
									detailMode === "basic"
										? "bg-card text-slate-900 shadow-xs dark:bg-slate-950 dark:text-slate-100"
										: "hover:text-slate-900 dark:hover:text-slate-100"
								}`}
							>
								{t({
									id: "playbookRuns.toggles.basicDetails",
									message: "Basic Details",
								})}
							</button>
							<button
								type="button"
								onClick={() => setDetailMode("full")}
								className={`rounded-xs px-2 py-1 font-medium transition-colors ${
									detailMode === "full"
										? "bg-card text-slate-900 shadow-xs dark:bg-slate-950 dark:text-slate-100"
										: "hover:text-slate-900 dark:hover:text-slate-100"
								}`}
							>
								{t({
									id: "playbookRuns.toggles.fullDetails",
									message: "Full Details",
								})}
							</button>
						</div>

						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={!selectedRun || selectedRunTasks.length === 0}
							onClick={handleDownloadCsv}
							className="h-8 text-xs gap-1.5"
						>
							<Download className="h-3.5 w-3.5" />
							{t({
								id: "playbookRuns.actions.downloadCsv",
								message: "Download CSV",
							})}
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{isMobile ? (
						<div className="space-y-4">
							{!selectedRun ? (
								<p>
									{t({
										id: "playbookRuns.tasksTable.noRunSelected",
										message: "No run selected.",
									})}
								</p>
							) : selectedRunTasks.length === 0 ? (
								<p>
									{t({
										id: "playbookRuns.tasksTable.noTasks",
										message: "No tasks found for this run.",
									})}
								</p>
							) : (
								paginatedTasks.map((task) => {
									const taskId = task.id || "";
									const taskAttempts = attemptsByTaskId.get(taskId) || [];
									const latestAttempt = taskAttempts[0];
									const attemptsExpanded =
										mobileAttempts[taskId] ?? showAttempts;
									const retrying = retryingTaskIds.has(taskId);
									const retryDisabled =
										!task.id ||
										retrying ||
										task.status === "dispatching" ||
										task.status === "in_progress";
									return (
										<article
											key={task.id}
											className="min-w-0 overflow-hidden rounded-lg border-[1.5px] border-card-border bg-card shadow-card"
										>
											<div className="flex items-start justify-between gap-3 border-b border-card-border p-4">
												<div className="space-y-2">
													<Badge
														variant="outline"
														className={statusBadgeClass(task.status)}
													>
														{statusLabel(task.status)}
													</Badge>
													{renderRecoveryReview(task)}
													<div className="text-xs text-muted-foreground">
														{t({
															id: "playbookRuns.tasksTable.lastAttempt",
															message: "Last Attempt",
														})}
														<div>
															{renderDateTimeParts(
																latestAttemptTime(latestAttempt),
															)}
														</div>
													</div>
												</div>
												{renderTaskActionsMenu({
													task,
													taskId,
													taskAttempts,
													attemptsExpanded,
													retrying,
													retryDisabled,
												})}
											</div>
											<div className="space-y-4 p-4">
												<section className="space-y-2">
													<h4 className="text-sm font-semibold">
														{t({
															id: "playbookRuns.tasksTable.inputs",
															message: "Inputs",
														})}
													</h4>
													{renderKeyValuePayload(
														filterPayloadForMode(
															task.input,
															detailMode,
															selectedRunInputSchemaFields,
															selectedRunInputHiddenFields,
														),
														undefined,
														selectedRunInputPhoneFields,
													)}
												</section>
												<section className="space-y-2 border-t border-card-border pt-4">
													<h4 className="text-sm font-semibold">
														{t({
															id: "playbookRuns.tasksTable.result",
															message: "Results",
														})}
													</h4>
													{renderKeyValuePayload(
														filterPayloadForMode(
															task.result || latestAttempt?.structuredResult,
															detailMode,
															selectedRunResultSchemaFields,
															selectedRunResultHiddenFields,
														),
														undefined,
														selectedRunResultPhoneFields,
													)}
												</section>
											</div>
											{taskAttempts.length > 0 && (
												<div className="border-t border-card-border p-4">
													<Button
														variant="outline"
														className="min-h-11 w-full"
														aria-expanded={attemptsExpanded}
														aria-controls={`mobile-attempts-${taskId}`}
														onClick={() =>
															setMobileAttempts((current) => ({
																...current,
																[taskId]: !attemptsExpanded,
															}))
														}
													>
														{attemptsExpanded
															? t({
																	id: "playbookRuns.toggles.hideAttempts",
																	message: "Hide Attempts",
																})
															: t({
																	id: "playbookRuns.toggles.showAttempts",
																	message: "Show Attempts",
																})}{" "}
														({taskAttempts.length})
													</Button>
													{attemptsExpanded && (
														<div
															id={`mobile-attempts-${taskId}`}
															className="mt-3 space-y-3"
														>
															{taskAttempts.map((attempt) =>
																renderAttempt(task, attempt),
															)}
														</div>
													)}
												</div>
											)}
										</article>
									);
								})
							)}
						</div>
					) : (
						<Table className="min-w-[900px] rounded-lg border">
							<TableHeader>
								<TableRow>
									<TableHead className="w-1 whitespace-nowrap">
										{t({
											id: "playbookRuns.tasksTable.status",
											message: "Status",
										})}
									</TableHead>
									<TableHead className="w-1 whitespace-nowrap">
										{t({
											id: "playbookRuns.tasksTable.lastAttempt",
											message: "Last Attempt",
										})}
									</TableHead>
									<TableHead className="w-[50%]">
										{t({
											id: "playbookRuns.tasksTable.inputs",
											message: "Inputs",
										})}
									</TableHead>
									<TableHead className="w-[50%]">
										{t({
											id: "playbookRuns.tasksTable.result",
											message: "Results",
										})}
									</TableHead>
									<TableHead className="w-1" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{!selectedRun ? (
									<TableRow>
										<TableCell colSpan={5}>
											{t({
												id: "playbookRuns.tasksTable.noRunSelected",
												message: "No run selected.",
											})}
										</TableCell>
									</TableRow>
								) : selectedRunTasks.length === 0 ? (
									<TableRow>
										<TableCell colSpan={5}>
											{t({
												id: "playbookRuns.tasksTable.noTasks",
												message: "No tasks found for this run.",
											})}
										</TableCell>
									</TableRow>
								) : (
									paginatedTasks.map((task, taskIndex) => {
										const taskAttempts =
											attemptsByTaskId.get(task.id || "") || [];
										const latestAttempt = taskAttempts[0];
										const taskId = task.id || "";
										const attemptsExpanded = showAttempts;
										const retrying = task.id
											? retryingTaskIds.has(task.id)
											: false;
										const retryDisabled =
											!task.id ||
											retrying ||
											task.status === "dispatching" ||
											task.status === "in_progress";
										const isEven = taskIndex % 2 === 0;
										const taskBg = isEven ? "bg-slate-100" : "bg-card";
										const attemptsBg = taskBg;
										return (
											<Fragment key={task.id}>
												<TableRow
													className={`border-t-2 border-t-slate-400 border-b border-b-slate-200/40 ${taskBg} hover:${taskBg}`}
												>
													<TableCell className="w-1 whitespace-nowrap align-top">
														<Badge
															variant="outline"
															className={statusBadgeClass(task.status)}
														>
															{statusLabel(task.status)}
														</Badge>
														{renderRecoveryReview(task)}
													</TableCell>
													<TableCell className="w-1 align-top">
														{renderDateTimeParts(
															latestAttemptTime(latestAttempt),
														)}
													</TableCell>
													<TableCell className="w-[50%] align-top">
														{renderKeyValuePayload(
															filterPayloadForMode(
																task.input,
																detailMode,
																selectedRunInputSchemaFields,
																selectedRunInputHiddenFields,
															),
															undefined,
															selectedRunInputPhoneFields,
														)}
													</TableCell>
													<TableCell className="w-[50%] align-top">
														{renderKeyValuePayload(
															filterPayloadForMode(
																task.result || latestAttempt?.structuredResult,
																detailMode,
																selectedRunResultSchemaFields,
																selectedRunResultHiddenFields,
															),
															undefined,
															selectedRunResultPhoneFields,
														)}
													</TableCell>
													<TableCell className="w-1 align-top">
														{renderTaskActionsMenu({
															task,
															taskId,
															taskAttempts,
															attemptsExpanded,
															retrying,
															retryDisabled,
														})}
													</TableCell>
												</TableRow>
												{showAttempts &&
													attemptsExpanded &&
													taskAttempts.length > 0 && (
														<TableRow
															className={`border-t-0 ${attemptsBg} hover:${attemptsBg}`}
														>
															<TableCell
																colSpan={5}
																className={`${attemptsBg} px-4 py-3`}
															>
																<div className="ms-4 border-s-2 border-slate-300 ps-4 space-y-3 py-1 dark:border-slate-700">
																	{taskAttempts.map((attempt) =>
																		renderAttempt(task, attempt),
																	)}
																</div>
															</TableCell>
														</TableRow>
													)}
											</Fragment>
										);
									})
								)}
							</TableBody>
						</Table>
					)}

					<div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
						<div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
							<span>
								{t({
									id: "playbookRuns.pagination.tasksPerPage",
									message: "Tasks per page:",
								})}
							</span>
							<Select
								value={String(tasksPerPage)}
								onValueChange={(val) => {
									setTasksPerPage(Number(val));
									setCurrentTasksPage(1);
								}}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="5">5</SelectItem>
									<SelectItem value="10">10</SelectItem>
									<SelectItem value="25">25</SelectItem>
									<SelectItem value="50">50</SelectItem>
									<SelectItem value="100">100</SelectItem>
								</SelectContent>
							</Select>
							{selectedRunTasks.length > 0 && (
								<span className="ml-2 text-xs text-slate-500">
									{t({ id: "showing", message: "Showing" })}{" "}
									{startTasksIndex + 1}–
									{Math.min(endTasksIndex, selectedRunTasks.length)}{" "}
									{t({ id: "of", message: "of" })} {selectedRunTasks.length}
								</span>
							)}
						</div>

						{totalTasksPages > 1 && (
							<Pagination className="mx-0 w-auto">
								<PaginationContent>
									<PaginationItem>
										<PaginationPrevious
											onClick={() =>
												setCurrentTasksPage((p) => Math.max(1, p - 1))
											}
											className={
												currentTasksPage === 1
													? "pointer-events-none opacity-50"
													: "cursor-pointer"
											}
										/>
									</PaginationItem>

									{Array.from({ length: totalTasksPages }, (_, i) => i + 1)
										.filter((page) => {
											if (page === 1 || page === totalTasksPages) return true;
											if (Math.abs(page - currentTasksPage) <= 1) return true;
											return false;
										})
										.map((page, index, array) => (
											<Fragment key={page}>
												{index > 0 && array[index - 1] !== page - 1 && (
													<PaginationItem key={`ellipsis-${page}`}>
														<PaginationEllipsis />
													</PaginationItem>
												)}
												<PaginationItem>
													<PaginationLink
														onClick={() => setCurrentTasksPage(page)}
														isActive={currentTasksPage === page}
														className="cursor-pointer"
													>
														{page}
													</PaginationLink>
												</PaginationItem>
											</Fragment>
										))}

									<PaginationItem>
										<PaginationNext
											onClick={() =>
												setCurrentTasksPage((p) =>
													Math.min(totalTasksPages, p + 1),
												)
											}
											className={
												currentTasksPage === totalTasksPages
													? "pointer-events-none opacity-50"
													: "cursor-pointer"
											}
										/>
									</PaginationItem>
								</PaginationContent>
							</Pagination>
						)}
					</div>
				</CardContent>
			</Card>

			<Dialog
				open={technicalDetails !== null}
				onOpenChange={(open) => {
					if (!open) setTechnicalDetails(null);
				}}
			>
				<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
					<DialogHeader>
						<DialogTitle>
							{technicalDetails?.title ||
								t({
									id: "playbookRuns.technicalDetails.title",
									message: "Technical details",
								})}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "playbookRuns.technicalDetails.description",
								message: "Raw document data for debugging.",
							})}
						</DialogDescription>
					</DialogHeader>
					<pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded bg-slate-950 p-4 text-xs text-slate-50">
						{technicalDetails
							? JSON.stringify(technicalDetails.document, null, 2)
							: ""}
					</pre>
				</DialogContent>
			</Dialog>

			<Dialog open={newRunDialogOpen} onOpenChange={setNewRunDialogOpen}>
				<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{t({
								id: "playbookRuns.newRun.title",
								message: "New Playbook Run",
							})}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "playbookRuns.newRun.description",
								message:
									"Submit runs using the selected Playbook schema manually or via CSV/Excel upload.",
							})}
						</DialogDescription>
					</DialogHeader>

					<Tabs
						value={newRunTab}
						onValueChange={(v) => setNewRunTab(v as "single" | "batch")}
					>
						<TabsList className="w-full">
							<TabsTrigger value="single" className="flex-1">
								{t({
									id: "playbookRuns.newRun.singleTab",
									message: "Single Run",
								})}
							</TabsTrigger>
							<TabsTrigger value="batch" className="flex-1">
								{t({
									id: "playbookRuns.newRun.batchTab",
									message: "Batch Upload (CSV/Excel)",
								})}
							</TabsTrigger>
						</TabsList>

						<form onSubmit={handleSubmitNewRun} className="space-y-4 py-2">
							<div className="space-y-2">
								<Label htmlFor="new-run-playbook">
									{t({
										id: "playbookRuns.newRun.playbook",
										message: "Playbook",
									})}
								</Label>
								<Select
									value={newRunPlaybookId}
									onValueChange={setNewRunPlaybookId}
									disabled={submittingRun}
								>
									<SelectTrigger id="new-run-playbook">
										<SelectValue
											placeholder={t({
												id: "playbookRuns.newRun.playbookPlaceholder",
												message: "Select a Playbook",
											})}
										/>
									</SelectTrigger>
									<SelectContent>
										{enabledPlaybooks.map((playbook) => (
											<SelectItem key={playbook.id} value={playbook.id!}>
												{playbook.name || playbook.id}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<TabsContent value="single" className="space-y-4 pt-2">
								<div className="grid gap-4 sm:grid-cols-2">
									{newRunFields.map((field) => {
										const value = newRunValues[field.name];
										const fieldId = `new-run-field-${field.name}`;
										return (
											<div
												key={field.name}
												className={
													field.type === "boolean"
														? "space-y-2 sm:col-span-2"
														: "space-y-2"
												}
											>
												<Label htmlFor={fieldId}>{fieldLabel(field)}</Label>
												{field.enumValues.length > 0 ? (
													<Select
														value={typeof value === "string" ? value : ""}
														onValueChange={(nextValue) =>
															handleNewRunFieldChange(field, nextValue)
														}
														disabled={submittingRun}
													>
														<SelectTrigger id={fieldId}>
															<SelectValue
																placeholder={t({
																	id: "playbookRuns.newRun.selectValue",
																	message: "Select value",
																})}
															/>
														</SelectTrigger>
														<SelectContent>
															{field.enumValues.map((enumValue) => (
																<SelectItem key={enumValue} value={enumValue}>
																	{enumValue}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												) : field.type === "boolean" ? (
													<div className="flex h-10 items-center gap-2">
														<Checkbox
															id={fieldId}
															checked={value === true}
															onCheckedChange={(checked) =>
																handleNewRunFieldChange(field, checked === true)
															}
															disabled={submittingRun}
														/>
													</div>
												) : field.type === "string" &&
													field.description.length > 80 ? (
													<Textarea
														id={fieldId}
														value={typeof value === "string" ? value : ""}
														onChange={(event) =>
															handleNewRunFieldChange(field, event.target.value)
														}
														disabled={submittingRun}
														rows={3}
														{...getContentFieldProps(
															typeof value === "string" ? value : "",
															language === "he" ? "rtl" : "ltr",
														)}
													/>
												) : (
													<Input
														id={fieldId}
														type={
															field.type === "number"
																? "number"
																: field.type === "phone_number"
																	? "tel"
																	: field.type === "date"
																		? "date"
																		: field.type === "datetime"
																			? "datetime-local"
																			: "text"
														}
														value={typeof value === "string" ? value : ""}
														onChange={(event) =>
															handleNewRunFieldChange(field, event.target.value)
														}
														disabled={submittingRun}
														{...(field.type === "string"
															? getContentFieldProps(
																	typeof value === "string" ? value : "",
																	language === "he" ? "rtl" : "ltr",
																)
															: {
																	dir: "ltr" as const,
																	className: "text-left",
																})}
													/>
												)}
												{field.description && (
													<p className="text-xs leading-5 text-slate-500">
														{field.description}
													</p>
												)}
											</div>
										);
									})}
								</div>
							</TabsContent>

							<TabsContent value="batch" className="space-y-4 pt-2">
								<div className="space-y-2">
									<Label htmlFor="new-run-file-upload">
										{t({
											id: "playbookRuns.newRun.fileUploadLabel",
											message: "Upload CSV or Excel File (.csv, .xlsx, .xls)",
										})}
									</Label>
									<Input
										id="new-run-file-upload"
										type="file"
										accept=".csv, .xlsx, .xls"
										onChange={handleBatchFileChange}
										disabled={submittingRun}
									/>
									<p className="text-xs text-slate-500">
										{t({
											id: "playbookRuns.newRun.fileUploadHelp",
											message:
												"Row values are mapped to schema fields using field names or defined column names.",
										})}
									</p>
								</div>

								{batchParsing && (
									<div className="flex items-center gap-2 text-sm text-slate-500 py-2">
										<Loader2 className="h-4 w-4 animate-spin" />
										{t({
											id: "playbookRuns.newRun.parsingFile",
											message: "Parsing and validating file...",
										})}
									</div>
								)}

								{!batchParsing && batchResult && (
									<div className="space-y-3">
										{!batchResult.valid ? (
											<div className="rounded-md border border-destructive/30 bg-danger-tint p-4 text-danger-ink">
												<div className="flex items-center gap-2 font-medium text-sm">
													<AlertCircle className="h-4 w-4" />
													{i18n._({
														id: "playbookRuns.newRun.validationErrorsTitle",
														message: "Validation Errors Found ({count})",
														values: { count: batchResult.errors.length },
													})}
												</div>
												<ul className="mt-2 max-h-48 overflow-y-auto text-xs space-y-1 pl-4 list-disc">
													{batchResult.errors.map((err, idx) => (
														<li key={idx}>{err.message}</li>
													))}
												</ul>
											</div>
										) : (
											<div className="rounded-md border border-live/30 bg-live-tint p-4 text-foreground">
												<div className="flex items-center gap-2 font-medium text-sm">
													<CheckCircle2 className="h-4 w-4" />
													{i18n._({
														id: "playbookRuns.newRun.validationSuccessTitle",
														message:
															"File verified successfully! ({count} runs ready to schedule)",
														values: { count: batchResult.totalRows },
													})}
												</div>
												<div className="mt-2 text-xs opacity-90">
													<strong>Mapped Columns:</strong>{" "}
													{Object.entries(batchResult.mappedColumns)
														.map(([field, col]) => `${col} → ${field}`)
														.join(", ")}
												</div>
											</div>
										)}
									</div>
								)}

								{batchServerResponse && (
									<div className="rounded-md border p-4 space-y-2 bg-slate-50 dark:bg-slate-900/50 text-xs">
										<div className="font-semibold text-sm">
											{t({
												id: "playbookRuns.newRun.serverResponseTitle",
												message: "Server Execution Result",
											})}
										</div>
										<div>
											Created:{" "}
											<Badge variant="secondary">
												{batchServerResponse.createdCount ??
													batchServerResponse.taskIds?.length ??
													0}
											</Badge>{" "}
											| Failed:{" "}
											<Badge variant="destructive">
												{batchServerResponse.failedCount ?? 0}
											</Badge>
										</div>
										{batchServerResponse.rowResults &&
											batchServerResponse.rowResults.length > 0 && (
												<div className="max-h-40 overflow-y-auto space-y-1 pt-1">
													{batchServerResponse.rowResults.map((res) => (
														<div
															key={res.rowIndex}
															className="flex items-center justify-between border-b pb-1"
														>
															<span>Row {res.rowIndex + 1}</span>
															{res.status === "created" ? (
																<span className="font-medium text-foreground">
																	Scheduled ({res.taskId})
																</span>
															) : (
																<span className="text-danger-ink">
																	Failed: {res.errors?.join("; ")}
																</span>
															)}
														</div>
													))}
												</div>
											)}
									</div>
								)}
							</TabsContent>

							<div className="flex items-center gap-2 rounded-md border p-3">
								<Checkbox
									id="new-run-schedule-immediately"
									checked={scheduleImmediately}
									onCheckedChange={(checked) =>
										setScheduleImmediately(checked === true)
									}
									disabled={submittingRun}
								/>
								<Label htmlFor="new-run-schedule-immediately">
									{t({
										id: "playbookRuns.newRun.scheduleImmediately",
										message: "Schedule immediately",
									})}
								</Label>
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={() => setNewRunDialogOpen(false)}
									disabled={submittingRun}
								>
									{t({ id: "common.cancel", message: "Cancel" })}
								</Button>
								<Button
									type="submit"
									disabled={
										submittingRun ||
										!companyId ||
										!newRunPlaybook ||
										newRunFields.length === 0 ||
										(newRunTab === "batch" &&
											(batchParsing ||
												!batchResult ||
												!batchResult.valid ||
												batchResult.taskInputs.length === 0 ||
												batchServerResponse?.success))
									}
								>
									{submittingRun && (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									)}
									{newRunTab === "batch"
										? t({
												id: "playbookRuns.newRun.submitBatch",
												message: "Submit Batch Runs",
											})
										: t({
												id: "playbookRuns.newRun.submitSingle",
												message: "Submit Run",
											})}
								</Button>
							</DialogFooter>
						</form>
					</Tabs>
				</DialogContent>
			</Dialog>
		</div>
	);
}
